/**
 * AskTrouveOverlay.jsx — persistent floating button + slide-up panel
 * for the AR mirror (LiveCoach) flow.
 *
 * Sits as a fixed bottom-right pill on top of the camera viewfinder.
 * Tapping opens a 70%-height sheet from the bottom containing the
 * full TrouveChatPanel (chat, voice, image attach).
 *
 * Visibility:
 *   • Hidden by default for free users with no access — `fetchTrouveAccess`
 *     resolves the tier before mounting the button so we don't tease
 *     a feature the user can't use.
 *   • Always visible for Pro / ambassador / referred users.
 *
 * The button doesn't replace LiveCoach functionality — it's an
 * additive overlay. While the sheet is open, the AR mirror is still
 * tracking face landmarks underneath; the user can dismiss the sheet
 * with the close chevron and resume coaching at any time.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";
import TrouveChatPanel from "./TrouveChatPanel";
import { fetchTrouveAccess } from "../lib/api";
import { track } from "../lib/analytics";

const AskTrouveOverlay = ({ forceVisible = false } = {}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [accessVisible, setAccessVisible] = useState(null); // null | true | false

  useEffect(() => {
    // forceVisible=true short-circuits the access fetch so guest demo
    // surfaces (e.g. /live-coach-demo) can still show the GPT-vision
    // escalation entry point. The chat panel itself still enforces
    // its own gates, so this only un-hides the launcher.
    if (forceVisible) {
      // eslint-disable-next-line
      setAccessVisible(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const a = await fetchTrouveAccess();
        if (!cancelled) {
          // Show the button for ANY tier that has access OR any tier that
          // would benefit from the upgrade nudge (referred or unauth-pro).
          // For now: only show if `allowed` so we don't dangle a Pro perk
          // in front of free-no-attribution users.
          setAccessVisible(!!a?.allowed);
        }
      } catch {
        if (!cancelled) setAccessVisible(false);
      }
    })();
    return () => { cancelled = true; };
  }, [forceVisible]);

  const handleOpen = useCallback(() => {
    setOpen(true);
    track("trouve_chat_button_clicked", { surface: "ar_mirror" });
  }, []);

  // Hide entirely until we know the tier — avoids button flash for free users
  if (!accessVisible) return null;

  return (
    <>
      {/* Floating button — bottom-right of viewport */}
      <AnimatePresence>
        {!open && (
          <motion.button
            type="button"
            initial={{ opacity: 0, scale: 0.85, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 8 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            onClick={handleOpen}
            aria-label={t("trouveChat.mirrorButtonAria")}
            data-testid="ask-trouve-mirror-btn"
            className="fixed bottom-5 right-4 z-[60] flex items-center gap-2 bg-gradient-to-br from-[#9B6A6C] to-[#7d4f53] text-white pl-3 pr-4 py-3 rounded-full shadow-[0_10px_30px_-10px_rgba(155,106,108,0.6)] hover:shadow-[0_14px_36px_-10px_rgba(155,106,108,0.75)] active:scale-95 transition-all"
          >
            <span className="relative flex items-center justify-center h-7 w-7 rounded-full overflow-hidden ring-1 ring-white/30">
              <span className="absolute inline-flex h-full w-full rounded-full bg-white/30 animate-ping" />
              <img
                src="/trouve/conversation-avatar.png"
                alt=""
                className="relative h-full w-full object-cover rounded-full"
                onError={(e) => {
                  // Fallback to the original sparkles glyph if asset missing.
                  e.currentTarget.style.display = "none";
                  e.currentTarget.nextElementSibling.style.display = "block";
                }}
              />
              <Sparkles
                className="relative h-5 w-5 hidden"
                aria-hidden="true"
              />
            </span>
            <span className="text-sm font-medium tracking-wide">
              {t("trouveChat.buttonLabel")}
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Slide-up sheet */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="fixed inset-0 z-[70] bg-black/45 backdrop-blur-sm"
              onClick={() => setOpen(false)}
              data-testid="ask-trouve-sheet-backdrop"
            />
            <motion.div
              key="sheet"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 280 }}
              className="fixed inset-x-0 bottom-0 z-[80] h-[80vh] sm:h-[75vh] bg-[#FDFBF7] rounded-t-3xl shadow-[0_-20px_60px_-20px_rgba(0,0,0,0.35)] overflow-hidden flex flex-col"
              data-testid="ask-trouve-sheet"
              role="dialog"
              aria-modal="true"
            >
              <div className="mx-auto mt-2 mb-1 h-1.5 w-12 rounded-full bg-[#EADDD7]" aria-hidden="true" />
              <div className="flex-1 min-h-0">
                <TrouveChatPanel
                  onClose={() => setOpen(false)}
                  dataTestidPrefix="ask-trouve-chat"
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default AskTrouveOverlay;
